import { escapeHtml, discountPercent } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  lineCount,
  price,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuFamily,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Tangerine, layout A: a bold diagonal-striped panel down the left with the logo, a huge thin
 * title and a few lines about the menu; on the white right, the dishes as cards in two columns,
 * each a round photo beside a bold name, its description and the price in red.
 *
 * The look (striped panel, cards, headings) is shared with tangerine-list and tangerine-banner.
 */

/* ------------------------------------------------------------------ shared look */

const svg = (markup: string) => `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;

/** A flame over a grill line: the emblem drawn when the board has no logo. */
const FLAME = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 60' fill='#fff'>" +
    "<path d='M30 4c3 9 13 14 13 27 0 8-6 14-13 14s-13-6-13-14c0-6 3-10 6-13 0 5 2 8 5 9-2-9 0-16 2-23z'/>" +
    "<path d='M30 26c2 4 6 6 6 11 0 4-3 6-6 6s-6-2-6-6c0-3 2-5 3-6 0 2 1 3 2 4 0-4 0-6 1-9z' fill='#f2661c' fill-opacity='.55'/>" +
    "<rect x='8' y='49' width='44' height='3.4' rx='1.7'/><rect x='14' y='55' width='32' height='3' rx='1.5'/></svg>",
);

export const TG_FAMILY: Omit<MenuFamily, 'layout'> = { id: 'tangerine', name: 'Tangerine' };

export const TG_THEME: MenuTheme = {
  backgroundColor: '#ffffff',
  surfaceColor: '#fff3ea',
  textColor: '#1f1d1b',
  primaryColor: '#f2661c',
  accentColor: '#d42a1e',
  fontId: 'cafe',
  backgroundMediaId: null,
};

export const TG_VARIANTS: MenuStyleVariant[] = [
  { id: 'tangerine', name: 'Tangerine & white', theme: {} },
  { id: 'tomato', name: 'Tomato red & white', theme: { primaryColor: '#d7322a', accentColor: '#d7322a' } },
  { id: 'basil', name: 'Basil green & cream', theme: { primaryColor: '#2f8f4e', accentColor: '#d9541e', backgroundColor: '#fffaf1', surfaceColor: '#f4efe2' } },
  { id: 'navy', name: 'Navy & white', theme: { primaryColor: '#1f3a68', accentColor: '#e0602a' } },
];

/** The striped colour panel: brand colour with lighter diagonal bands. */
export function tgStripesCss(S: string, cls: string): string {
  return `
${S} .${cls}{position:relative;color:#fff;background-color:var(--mb-primary);
  background-image:repeating-linear-gradient(-55deg,rgba(255,255,255,.09) 0 3.2em,transparent 3.2em 6.4em),linear-gradient(160deg,rgba(255,255,255,.12),rgba(0,0,0,.12))}`;
}

/** The board's logo, or a drawn flame over its name. */
export function tgEmblem(ctx: RenderContext, cls: string): string {
  const { brand } = ctx.board;
  const logo = image(ctx, brand.logoMediaId, `${cls}-logo`);
  if (logo) return `<div class="${cls}">${logo}</div>`;
  const name = ctx.showEn ? brand.name?.trim() || brand.nameAr?.trim() : brand.nameAr?.trim() || brand.name?.trim();
  if (!name) return '';
  return `<div class="${cls}"><i class="${cls}-flame"></i><p class="${cls}-name" dir="auto">${escapeHtml(name)}</p></div>`;
}

export function tgEmblemCss(S: string, cls: string, size: number): string {
  return `
${S} .${cls}{display:flex;flex-direction:column;align-items:center;min-width:0}
${S} .${cls}-logo{display:block;max-width:${size * 1.6}em;max-height:${size}em;object-fit:contain}
${S} .${cls}-flame{width:${(size * 0.62).toFixed(2)}em;height:${(size * 0.62).toFixed(2)}em;background:${FLAME} center/contain no-repeat}
${S} .${cls}-name{max-width:100%;font-family:var(--mb-font-script);font-size:${(size * 0.21).toFixed(2)}em;line-height:1.25;margin-top:-.15em;transform:rotate(-4deg);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-shadow:0 .05em .2em rgba(0,0,0,.25)}`;
}

/** Title in thin tall capitals, the Arabic under it. Two-line titles fit their longest word. */
export function tgTitle(ctx: RenderContext, cls: string, maxChars: number): string {
  const title = (ctx.board.settings.title ?? ctx.board.name ?? '').trim();
  const longest = title.split(/\s+/).reduce((a, w) => (w.length > a.length ? w : a), '');
  return `<div class="${cls}s" style="font-size:${fitScale(longest, maxChars, 0.5).toFixed(3)}em">${bilingual(ctx, title, ctx.board.settings.titleAr, cls)}</div>`;
}

/** Tagline and the page's section notes, as the panel's paragraphs. */
export function tgBlurb(ctx: RenderContext, cls: string): string {
  const { board } = ctx;
  const notes = ctx.page.columns
    .flat()
    .filter((b) => !b.continued)
    .map((b) => bilingual(ctx, b.section.subtitle, b.section.subtitleAr, cls))
    .filter(Boolean);
  return [bilingual(ctx, board.brand.tagline, board.brand.taglineAr, cls), ...notes].join('');
}

export function tgPanelCss(S: string, p: string, titleEm: number): string {
  return `
${S} .${p}-title{font-family:var(--mb-font-heading);font-weight:500;font-size:${titleEm}em;line-height:1.04;text-transform:uppercase;letter-spacing:.01em;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;padding-top:.05em}
${S} .${p}-title.mb-ar{line-height:1.35;-webkit-line-clamp:2}
${S} .${p}-title.mb-2nd{font-size:${(titleEm * 0.5).toFixed(2)}em;margin-top:.15em}
${S} .${p}-blurb{font-size:1.35em;line-height:1.45;opacity:.95;margin-top:1.1em;
  display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
${S} .${p}-blurb.mb-2nd{margin-top:.4em;-webkit-line-clamp:3}`;
}

/** Section heading: accent capitals over a rule. */
export function tgHead(ctx: RenderContext, b: MenuBlock, cls = 'tg-head'): string {
  return hasTitle(b.section) ? `<div class="${cls}">${sectionTitle(ctx, b.section, 'tg-ht')}<i class="tg-hrule"></i></div>` : '';
}

export function tgHeadHeight(board: MenuBoardContent, section: MenuSection): number {
  if (!hasTitle(section)) return 0;
  const both = board.settings.language === 'both' && section.title?.trim() && section.titleAr?.trim();
  return 1.7 * 1.25 + (both ? 1.25 * 1.3 * ARABIC_LINE : 0) + 0.6 + 1.4;
}

export function tgHeadCss(S: string): string {
  return `
${S} .tg-head{display:flex;flex-wrap:wrap;align-items:baseline;column-gap:.8em;margin-bottom:1.4em}
${S} .tg-ht{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.7em;line-height:1.25;letter-spacing:.12em;text-transform:uppercase;
  color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
${S} .tg-ht.mb-2nd{font-size:1.25em;letter-spacing:0;line-height:1.3}
${S} .tg-hrule{flex:1 0 100%;height:.22em;margin-top:.5em;background:linear-gradient(90deg,var(--mb-primary) 0 4em,color-mix(in srgb,var(--mb-text) 14%,transparent) 4em)}
${S}[dir=rtl] .tg-hrule{background:linear-gradient(270deg,var(--mb-primary) 0 4em,color-mix(in srgb,var(--mb-text) 14%,transparent) 4em)}`;
}

/** Sizes side by side, or the selling price with the original struck through. */
export function tgPrice(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  if (!item.variants.length) return `<p class="${cls}">${sellingPrice(ctx, item, `${cls}-v`)}</p>`;
  return `<p class="${cls}">${item.variants
    .map((v) => `<span class="${cls}-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join('')}</p>`;
}

/** Name in bold capitals, the Arabic under it, a badge beside. */
export function tgNames(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<p class="${cls}-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</p>`
    : `<p class="${cls}-name">${escapeHtml(item.title)}</p>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="${cls}-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const badges = badge(ctx, item, 'tg-badge');
  return `${badges ? `<div class="tg-badges">${badges}</div>` : ''}${name}${second}`;
}

/** A dish card: round photo beside (or above) the name, description and red price. */
export function tgCard(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  const photo = image(ctx, item.imageMediaId, 'tg-img');
  return [
    `<div class="${cls}">`,
    `<div class="tg-photo${photo ? '' : ' tg-nophoto'}">${photo}</div>`,
    `<div class="${cls}-text">`,
    tgNames(ctx, item, 'tg'),
    description(ctx, item, 'tg-desc'),
    tgPrice(ctx, item, 'tg-price'),
    '</div></div>',
  ].join('');
}

/** Text height of a card in em, matching tgCardCss. */
export function tgTextHeight(board: MenuBoardContent, item: MenuItemContent, perLine: number, maxLines: number): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const badges = item.badge || discountPercent(item) !== null ? 1.5 : 0;
  const second = en && ar && item.titleAr?.trim() ? 1.25 * 1.3 * ARABIC_LINE : 0;
  const lines = board.settings.showDescriptions
    ? (en || !item.descriptionAr ? lineCount(item.description, perLine, maxLines) : 0) +
      (ar ? lineCount(item.descriptionAr, perLine, en ? 2 : maxLines) * ARABIC_LINE : 0)
    : 0;
  return badges + 1.6 * 1.15 + second + (lines ? 0.3 + lines * 1 * 1.35 : 0) + 0.35 + 1.6 * 1.2;
}

export function tgCardCss(S: string): string {
  return `
${S} .tg-photo{position:relative;flex:0 0 auto;border-radius:50%;overflow:hidden;background:var(--mb-surface);
  box-shadow:0 .6em 1.2em rgba(0,0,0,.18),0 0 0 .3em var(--mb-bg),0 0 0 .45em color-mix(in srgb,var(--mb-primary) 35%,transparent)}
${S} .tg-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
${S} .tg-nophoto{background:radial-gradient(circle at 35% 30%,var(--mb-bg),var(--mb-surface) 70%)}
${S} .tg-badges{display:flex;gap:.3em;margin-bottom:.3em}
${S} .tg-badge{font-size:.8em;font-weight:800;padding:.15em .6em;border-radius:.2em;background:var(--mb-primary);color:#fff;white-space:nowrap}
${S} .tg-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.6em;line-height:1.15;text-transform:uppercase;letter-spacing:.02em;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .tg-name.mb-ar{line-height:1.3}
${S} .tg-name2{font-family:var(--mb-font-heading);font-size:1.25em;line-height:1.3;font-weight:700;opacity:.8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .tg-desc{font-size:1em;line-height:1.35;opacity:.82;margin-top:.3em;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
${S} .tg-desc.mb-2nd{-webkit-line-clamp:2;margin-top:.15em}
${S} .tg-price{display:flex;flex-wrap:wrap;align-items:baseline;gap:.1em 1em;margin-top:.35em;color:var(--mb-accent);
  font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.6em;line-height:1.2;white-space:nowrap}
${S} .tg-price-v-old{font-size:.7em;opacity:.55;color:var(--mb-text);margin-inline-start:.5em}
${S} .tg-price-size{white-space:nowrap}
${S} .tg-price-size small{font-size:.6em;letter-spacing:.06em;text-transform:uppercase;color:var(--mb-text);opacity:.7}`;
}

/** Cards that share a row cost the tallest of them. */
export function tgRows(heights: number[], perRow: number): number {
  let total = 0;
  for (let i = 0; i < heights.length; i += perRow) total += Math.max(...heights.slice(i, i + perRow));
  return total;
}

/* ------------------------------------------------------------------ layout A */

const S = '.mb-t-tangerine-split';
const TITLE_EM = 6.4;
const PHOTO = 9.5;
/** Height of the striped band across the top in portrait. */
const PORTRAIT_BAND = 31;

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const perLine = portrait ? 30 : 38;
  return {
    capacity: portrait ? 120 - PORTRAIT_BAND - 6 : 67.5 - 6,
    blockCost: (section, items) =>
      tgHeadHeight(board, section) + tgRows(items.map((i) => Math.max(PHOTO, tgTextHeight(board, i, perLine, 3)) + 2.2), 2) + 0.4,
  };
}

function section(ctx: RenderContext, b: MenuBlock): string {
  return `<div class="tg1-sec">${tgHead(ctx, b)}<div class="tg1-cards">${b.items.map((i) => tgCard(ctx, i, 'tg1-card')).join('')}</div></div>`;
}

export const tangerineSplit: MenuTemplate = {
  id: 'tangerine-split',
  name: 'Tangerine · Split and cards',
  description:
    'A bold diagonal-striped panel with the logo, a huge thin title and a few lines about the menu; on the white side, dishes as cards in two columns: round photo, bold name, description and red price.',
  defaultTheme: TG_THEME,
  variants: TG_VARIANTS,
  cuisines: ['burgers', 'cafe', 'pizza'],
  family: { ...TG_FAMILY, layout: 'Split and cards' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), [column(board, orientation === 'portrait')]);
  },

  render(ctx) {
    const portrait = ctx.orientation === 'portrait';
    const body = ctx.page.columns.flat().map((b) => section(ctx, b)).join('');
    return [
      '<div class="tg1-panel">',
      tgEmblem(ctx, 'tg1-emblem'),
      `<div class="tg1-words">${tgTitle(ctx, 'tg1-title', portrait ? 11 : 8)}${tgBlurb(ctx, 'tg1-blurb')}</div>`,
      '</div>',
      `<div class="tg1-body">${body}</div>`,
    ].join('');
  },

  css: `
${S}{flex-direction:row}
${S}.mb-portrait{flex-direction:column}
${tgStripesCss(S, 'tg1-panel')}
${S} .tg1-panel{flex:0 0 35%;min-width:0;display:flex;flex-direction:column;padding:3.5em 3.2em;gap:2.4em}
${S}.mb-portrait .tg1-panel{flex:0 0 ${PORTRAIT_BAND}em;flex-direction:row;align-items:center;padding:2.5em 3em}
${tgEmblemCss(S, 'tg1-emblem', 11)}
${S} .tg1-emblem{align-self:flex-start}
${S}.mb-portrait .tg1-emblem{align-self:center;flex:0 0 14em}
${S} .tg1-words{min-width:0;flex:1 1 auto;display:flex;flex-direction:column;justify-content:center}
${tgPanelCss(S, 'tg1', TITLE_EM)}
${S}.mb-portrait .tg1-title:not(.mb-2nd){font-size:5.4em}
${S}.mb-portrait .tg1-title{-webkit-line-clamp:2}
${S}.mb-portrait .tg1-blurb{-webkit-line-clamp:2;margin-top:.7em}
${S} .tg1-body{flex:1 1 auto;min-width:0;min-height:0;padding:3em 3em}
${tgHeadCss(S)}
${tgCardCss(S)}
${S} .tg1-cards{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:3em}
${S} .tg1-card{display:flex;align-items:flex-start;gap:1.6em;min-width:0;margin-bottom:2.2em}
${S} .tg1-card .tg-photo{width:${PHOTO}em;height:${PHOTO}em}
${S} .tg1-card-text{flex:1 1 auto;min-width:0}
`,
};
